/* hs-sections-a.js — behaviours for hs-marquee, hs-card-carousel and hs-accordion-cards.
   One owner per behaviour (R-36): marquee loop time ([data-hs-mq]), snap-rail carousels
   ([data-hs-cc]) and flip cards ([data-hs-flip-item]). CTAs / scroll-to-top stay in hs-content.js.
   Nothing on the scroll path reads layout (R-89): the active slide comes from an
   IntersectionObserver; layout is read only at init, on a breakpoint change, on a ResizeObserver
   callback or on a click. Arrows name a TARGET INDEX and align that slide (R-43), never scrollBy.
   Loaded deferred by each section; the guard makes repeated tags a no-op. Theme editor:
   shopify:section:load re-inits, shopify:block:select scrolls the rail to the selected card. */
(function () {
  'use strict';
  if (window.__hsSectionsA) return; window.__hsSectionsA = true;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var each = function (list, fn) { Array.prototype.forEach.call(list, fn); };

  /* ---------- marquee: loop time = one group's width ÷ speed (px/s) ---------- */
  function initMarquee(root) {
    if (root.__hsMq) return; root.__hsMq = true;
    var track = root.querySelector('[data-hs-mq-track]');
    var group = root.querySelector('[data-hs-mq-group]');
    var speed = parseFloat(root.getAttribute('data-speed')) || 45;
    if (!track || !group || speed <= 0) return;
    function set() {
      var w = group.getBoundingClientRect().width;
      if (w > 0) track.style.setProperty('--hs-mq-dur', (w / speed).toFixed(2) + 's');
    }
    set();
    if ('ResizeObserver' in window) new ResizeObserver(set).observe(group);
  }

  /* ---------- card carousel: native scroll-snap rail + dots + arrows ---------- */
  function initRail(root) {
    if (root.__hsCc) return;
    var rail = root.querySelector('[data-hs-rail]');
    if (!rail) return;
    root.__hsCc = true;
    var slides = [].slice.call(rail.querySelectorAll('[data-hs-slide]'));
    var dots = [].slice.call(root.querySelectorAll('[data-hs-dot]'));
    var prev = root.querySelector('[data-hs-prev]');
    var next = root.querySelector('[data-hs-next]');
    var loop = root.getAttribute('data-loop') === 'true';
    var ratio = slides.map(function () { return 0; });
    var active = 0, positions = slides.length, on = false;
    if (!slides.length) return;

    function paint() {
      dots.forEach(function (d, i) {
        if (i === active) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
      });
      if (prev) prev.setAttribute('aria-disabled', !loop && active <= 0 ? 'true' : 'false');
      if (next) next.setAttribute('aria-disabled', !loop && active >= positions - 1 ? 'true' : 'false');
    }

    /* Layout reads live here only — never on scroll. */
    function measure() {
      on = getComputedStyle(rail).getPropertyValue('--hs-on').trim() === '1';
      var per = 1;
      if (on) {
        var cs = getComputedStyle(rail);
        var gap = parseFloat(cs.columnGap) || 0;
        var cw = rail.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
        var sw = slides[0].getBoundingClientRect().width;
        if (sw > 0) per = Math.max(1, Math.floor((cw + gap + 1) / (sw + gap)));
      }
      positions = on ? Math.max(1, slides.length - per + 1) : 1;
      dots.forEach(function (d, i) { d.hidden = i >= positions; });
      if (on) { rail.setAttribute('tabindex', '0'); } else { rail.removeAttribute('tabindex'); }
      if (active > positions - 1) active = positions - 1;
      paint();
    }

    function go(i) {
      if (!on) return;
      if (loop) { if (i < 0) i = positions - 1; else if (i > positions - 1) i = 0; }
      else { i = Math.max(0, Math.min(positions - 1, i)); }
      var pad = parseFloat(getComputedStyle(rail).scrollPaddingLeft) || 0;
      var left = slides[i].offsetLeft - pad; /* the rail is position:relative → it is the offsetParent */
      active = i; paint();
      rail.scrollTo({ left: Math.max(0, left), behavior: reduce.matches ? 'auto' : 'smooth' });
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { ratio[slides.indexOf(e.target)] = e.intersectionRatio; });
        if (!on) return;
        for (var i = 0; i < ratio.length; i++) {
          if (ratio[i] >= 0.6) { active = Math.min(i, positions - 1); paint(); return; }
        }
      }, { root: rail, threshold: [0, 0.6, 1] });
      slides.forEach(function (s) { io.observe(s); });
    }

    if (prev) prev.addEventListener('click', function () { go(active - 1); });
    if (next) next.addEventListener('click', function () { go(active + 1); });
    dots.forEach(function (d) {
      d.addEventListener('click', function () { go(parseInt(d.getAttribute('data-hs-dot'), 10) || 0); });
    });
    rail.addEventListener('keydown', function (e) {
      if (!on) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); go(active + 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); go(active - 1); }
    });

    var mq = window.matchMedia('(max-width: 768px)');
    if (mq.addEventListener) mq.addEventListener('change', measure); else if (mq.addListener) mq.addListener(measure);
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(rail);
    measure();

    root.__hsGoTo = function (slide) {
      var i = slides.indexOf(slide);
      if (i >= 0) go(Math.min(i, positions - 1));
    };
  }

  /* ---------- flip cards: the whole card toggles; the + is the real button ---------- */
  function setOpen(item, open) {
    item.setAttribute('data-anim', '');
    if (open) item.setAttribute('data-open', ''); else item.removeAttribute('data-open');
    var b = item.querySelector('[data-hs-flip]');
    if (b) b.setAttribute('aria-expanded', open ? 'true' : 'false');
  }
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var item = t.closest('[data-hs-flip-item]');
    if (!item || t.closest('a')) return; /* links inside the hidden text keep working */
    var open = item.hasAttribute('data-open');
    var root = item.closest('[data-hs-ac]');
    if (!open && root && root.getAttribute('data-single') === 'true') {
      each(root.querySelectorAll('[data-hs-flip-item][data-open]'), function (o) { if (o !== item) setOpen(o, false); });
    }
    setOpen(item, !open);
  });

  /* ---------- boot ---------- */
  function initAll(scope) {
    var s = scope || document;
    each(s.querySelectorAll('[data-hs-mq]'), initMarquee);
    each(s.querySelectorAll('[data-hs-cc]'), initRail);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); });
  else initAll();
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    var slide = e.target && e.target.closest && e.target.closest('[data-hs-slide]');
    var root = slide && slide.closest('[data-hs-cc]');
    if (root && root.__hsGoTo) root.__hsGoTo(slide);
  });
})();
